import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// i18next v25 + CRA: default instance can be typed as a narrowed `i18n` interface; runtime has the full API.
const i18n = i18next as any

/**
 * URL for `public/locales/{lang}.json` (Vite serves `public/` at `import.meta.env.BASE_URL`).
 * Example: `/locales/en.json` or `/my-app/locales/en.json` when `base` is set.
 */
export function localeJsonUrl(lang: string): string {
  const base = import.meta.env.BASE_URL ?? '/'
  // Locale JSON is NEVER cached, in dev and in production alike. A client that
  // has the new JS bundle but a stale `/locales/*.json` renders raw `a.b.c`
  // keys in the UI (this is what shipped the `weaponLifespan.daysShort` pill).
  // The unique query busts every URL-keyed layer — browser HTTP cache, service
  // worker, and any CDN edge copy that would otherwise answer for the old file.
  return `${base}locales/${lang}.json?v=${Date.now()}`
}

/**
 * In-flight + resolved locale bundles for this page session.
 * Dedupes concurrent `fetchLocaleJson(lang)` (e.g. init + StartPage) and avoids repeat network after success.
 */
const localeJsonFetchByLang = new Map<string, Promise<Record<string, unknown>>>()

/**
 * Fetch a locale JSON from the public folder (session-level promise cache).
 *
 * `cache: 'no-store'` keeps the response out of the HTTP cache entirely and refuses to
 * read from it, so no layer can answer with a locale file older than the running bundle
 * (raw `a.b.c` keys in the UI). Paired with the per-request query bust in localeJsonUrl.
 * Trade-off: every page load re-downloads the active language in full — no 304s.
 */
export async function fetchLocaleJson(lang: string): Promise<Record<string, unknown>> {
  const hit = localeJsonFetchByLang.get(lang)
  if (hit) return hit

  const promise = (async (): Promise<Record<string, unknown>> => {
    const res = await fetch(localeJsonUrl(lang), { cache: 'no-store' })
    if (!res.ok) {
      throw new Error(`Failed to load locale ${lang}: ${res.status} ${res.statusText}`)
    }
    return (await res.json()) as Record<string, unknown>
  })()

  localeJsonFetchByLang.set(lang, promise)
  promise.catch(() => {
    localeJsonFetchByLang.delete(lang)
  })

  return promise
}

const getSavedLanguage = (): string => {
  if (typeof window === 'undefined') return 'en'
  const saved = localStorage.getItem('i18nextLng')
  return saved || 'en'
}

const loadLanguageAsync = async (lang: string): Promise<void> => {
  if (i18n.hasResourceBundle(lang, 'translation')) return
  try {
    const bundle = await fetchLocaleJson(lang)
    i18n.addResourceBundle(lang, 'translation', bundle, true, true)
  } catch {
    // Language file missing — will silently fall back to English
  }
}

let initPromise: Promise<void> | null = null

/**
 * Loads `/locales/en.json` from `public/`, then initializes i18next.
 * Call once from `index.tsx` before `root.render` so `t()` always has resources.
 */
export function initI18n(): Promise<void> {
  if (initPromise) return initPromise
  initPromise = (async () => {
    const initialLanguage = getSavedLanguage()
    let en: Record<string, unknown>
    try {
      en = await fetchLocaleJson('en')
    } catch (e) {
      console.error('[i18n] Failed to load /locales/en.json from public folder', e)
      en = {}
    }

    await i18n.use(LanguageDetector).use(initReactI18next).init({
      resources: {
        en: { translation: en },
      },
      lng: initialLanguage,
      fallbackLng: 'en',
      debug: false,
      interpolation: { escapeValue: false },
      detection: {
        order: ['localStorage'],
        caches: ['localStorage'],
        lookupLocalStorage: 'i18nextLng',
      },
    })

    if (typeof window !== 'undefined') {
      const activeLang = initialLanguage !== 'en' ? initialLanguage : null
      if (activeLang) {
        void loadLanguageAsync(activeLang)
      }

      const ALL_LANGS = ['zh', 'ja', 'ru', 'ar']
      const toPreload = ALL_LANGS.filter((l) => l !== activeLang)
      setTimeout(() => {
        toPreload.forEach((lang) => void loadLanguageAsync(lang))
      }, 3000)

      // DEV: hot-refresh the active locale whenever the tab regains focus, so
      // strings edited while the session is open appear without a full reload
      // (bundles otherwise load exactly once per page session).
      if (import.meta.env.DEV) {
        window.addEventListener('focus', () => {
          const lng = (i18n.language as string) || 'en'
          localeJsonFetchByLang.delete(lng)
          void fetchLocaleJson(lng)
            .then((bundle) => {
              i18n.addResourceBundle(lng, 'translation', bundle, true, true)
              // Re-emits languageChanged so react-i18next consumers re-render.
              void i18n.changeLanguage(lng)
            })
            .catch(() => {})
        })
      }
    }
  })()
  return initPromise
}

export default i18n
